<template>
    <AppSidebar>
        <div class="space-y-10">
            <!-- Page Header -->
            <div class="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-8 border-b border-gray-100 dark:border-gray-800">
                <div>
                    <h1 class="text-3xl font-bold text-gray-900 dark:text-white leading-none mb-2">
                        Service Management
                    </h1>
                    <p class="text-sm text-gray-500 dark:text-gray-400 font-medium">
                        Manage your service offerings and pricing structures
                    </p>
                </div>
                <button
                    @click="showAddServiceModal = true"
                    class="inline-flex items-center px-6 py-3 bg-primary-600 text-white text-xs font-bold rounded-xl hover:bg-primary-700 transition-all shadow-lg shadow-primary-500/20 active:scale-95"
                >
                    <PlusIcon class="w-4 h-4 mr-2" />
                    Add New Service
                </button>
            </div>

            <!-- Services List -->
            <div class="bg-white dark:bg-gray-800/50 rounded-[40px] border border-gray-100 dark:border-gray-700/50 shadow-sm overflow-hidden">
                <div class="p-6 border-b border-gray-50 dark:border-gray-700/50">
                    <h2 class="text-sm font-bold text-gray-900 dark:text-white">Available Services</h2>
                </div>
                <div class="overflow-x-auto custom-scrollbar">
                    <table class="min-w-full divide-y divide-gray-100 dark:divide-gray-800">
                        <thead class="bg-gray-50/50 dark:bg-gray-900/40">
                            <tr>
                                <th class="px-8 py-4 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 tracking-wider">Service Name</th>
                                <th class="px-8 py-4 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 tracking-wider">Category</th>
                                <th class="px-8 py-4 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 tracking-wider">Starting Price</th>
                                <th class="px-8 py-4 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 tracking-wider">Status</th>
                                <th class="px-8 py-4 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 tracking-wider">Visibility</th>
                                <th class="px-8 py-4 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-50 dark:divide-gray-800/50">
                            <tr
                                v-for="service in serviceStore.services"
                                :key="service.id"
                                class="group hover:bg-gray-50/50 dark:hover:bg-gray-900/20 transition-all"
                            >
                                <td class="px-8 py-6">
                                    <div class="flex items-center">
                                        <div class="flex-shrink-0">
                                            <div class="w-12 h-12 bg-primary-50 dark:bg-primary-900/30 rounded-2xl flex items-center justify-center border border-primary-100/50 dark:border-primary-800/50 text-primary-600 dark:text-primary-400 font-bold text-lg">
                                                {{ service.name.charAt(0) }}
                                            </div>
                                        </div>
                                        <div class="ml-5">
                                            <div class="text-sm font-bold text-gray-900 dark:text-white tracking-tight">
                                                {{ service.name }}
                                            </div>
                                            <div class="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                                                {{ service.short_description }}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td class="px-8 py-6">
                                    <div class="flex flex-wrap gap-2">
                                        <span v-for="cat in (service.categories || (service.category ? [service.category] : []))" :key="cat.id" 
                                            class="px-3 py-1 text-[9px] font-black uppercase tracking-widest rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-100/50 dark:border-blue-800/50">
                                            {{ cat.name }}
                                        </span>
                                    </div>
                                </td>
                                <td class="px-8 py-6 text-sm font-bold text-gray-900 dark:text-white tracking-tight">
                                    ${{ service.pricing.starting_price.toLocaleString() }}
                                </td>
                                <td class="px-8 py-6">
                                    <span
                                        class="px-3 py-1 text-[10px] font-bold rounded-full transition-all border"
                                        :class="service.status === 'active'
                                                ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 border-emerald-100 dark:border-emerald-800'
                                                : 'bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 border border-rose-100 dark:border-rose-800'
                                        "
                                    >
                                        {{ service.status === 'active' ? 'Active' : 'Inactive' }}
                                    </span>
                                </td>
                                <td class="px-8 py-6">
                                    <button @click="togglePublic(service)"
                                        class="flex items-center gap-2 px-2.5 py-1 rounded-lg border transition-all active:scale-95"
                                        :class="service.public_status ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-400' : 'bg-gray-50/50 dark:bg-gray-800/50 border-gray-100 dark:border-gray-700 text-gray-400'">
                                        <div class="w-1.5 h-1.5 rounded-full" :class="service.public_status ? 'bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.5)]' : 'bg-gray-300'"></div>
                                        <span class="text-[10px] font-bold">{{ service.public_status ? 'Public' : 'Private' }}</span>
                                    </button>
                                </td>
                                <td class="px-8 py-6">
                                    <div class="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-all scale-95 group-hover:scale-100">
                                        <button
                                            @click="editService(service)"
                                            class="p-2.5 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-xl hover:bg-primary-600 hover:text-white transition-all shadow-sm"
                                        >
                                            <PencilSquareIcon class="w-4 h-4" />
                                        </button>
                                        <button
                                            @click="toggleServiceStatus(service)"
                                            class="p-2.5 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-xl hover:bg-amber-600 hover:text-white transition-all shadow-sm"
                                        >
                                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"></path></svg>
                                        </button>
                                        <button
                                            @click="deleteService(service)"
                                            class="p-2.5 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-xl hover:bg-rose-600 hover:text-white transition-all shadow-sm"
                                        >
                                            <TrashIcon class="w-4 h-4" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Add/Edit Service Modal -->
        <div v-if="showAddServiceModal || editingService" class="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div class="fixed inset-0 bg-gray-900/60 backdrop-blur-md transition-opacity" @click="closeModal"></div>
            <div class="relative bg-white dark:bg-gray-800 rounded-[40px] shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto custom-scrollbar border border-gray-100 dark:border-gray-700/50">
                <!-- Header -->
                <div class="sticky top-0 z-10 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md px-10 py-8 border-b border-gray-50 dark:border-gray-700/30 flex items-center justify-between">
                    <h2 class="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                        {{ editingService ? "Service Configuration" : "Add New Service" }}
                    </h2>
                    <button @click="closeModal" class="p-3 bg-gray-50 dark:bg-gray-700 rounded-2xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-all">
                        <svg class="w-6 h-6 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                    </button>
                </div>

                <form @submit.prevent="saveService" class="p-10 space-y-10">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-10">
                        <div class="space-y-6">
                            <h3 class="text-xs font-bold text-primary-600 uppercase tracking-widest border-b border-gray-50 dark:border-gray-700/30 pb-4">Basic Information</h3>
                            <div>
                                <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 px-1">Service Name*</label>
                                <input v-model="serviceForm.name" type="text" required class="w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3.5 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium" placeholder="Ex: Web Development" />
                            </div>
                            <div>
                                <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 px-1">Service Categories*</label>
                                <div class="grid grid-cols-1 gap-2 p-4 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl max-h-48 overflow-y-auto custom-scrollbar">
                                    <label v-for="category in serviceStore.categories" :key="category.id" class="flex items-center group cursor-pointer p-2 rounded-xl hover:bg-white dark:hover:bg-gray-800 transition-all">
                                        <input type="checkbox" :value="category.id" v-model="serviceForm.category_ids" class="w-4 h-4 rounded border-gray-300 dark:border-gray-700 text-primary-600 focus:ring-primary-500/20" />
                                        <span class="ml-3 text-[11px] font-bold text-gray-600 dark:text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors uppercase tracking-wider">{{ category.name }}</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div class="space-y-6">
                            <h3 class="text-xs font-bold text-primary-600 uppercase tracking-widest border-b border-gray-50 dark:border-gray-700/30 pb-4">Pricing & Timeline</h3>
                            <div>
                                <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 px-1">Starting Price ($)*</label>
                                <input v-model.number="serviceForm.pricing.starting_price" type="number" step="0.01" required class="w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3.5 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium" @input="validateStartingPrice" />
                                <p v-if="errors.starting_price" class="text-[10px] font-bold text-rose-500 mt-2 px-1">{{ errors.starting_price }}</p>
                            </div>
                            <div>
                                <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 px-1">Project Timeline (Weeks)*</label>
                                <div class="flex space-x-3">
                                    <div class="flex-1">
                                        <input v-model.number="serviceForm.timeline.min_weeks" type="number" min="1" required class="w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3.5 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium" placeholder="Min" @input="validateTimeline" />
                                    </div>
                                    <div class="flex-1">
                                        <input v-model.number="serviceForm.timeline.max_weeks" type="number" min="1" required class="w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3.5 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium" placeholder="Max" @input="validateTimeline" />
                                    </div>
                                </div>
                                <p v-if="errors.min_weeks || errors.max_weeks" class="text-[10px] font-bold text-rose-500 mt-2 px-1">{{ errors.min_weeks || errors.max_weeks }}</p>
                            </div>
                            <div class="flex items-center space-x-8 pt-4">
                                <label class="flex items-center group cursor-pointer">
                                    <div class="relative">
                                        <input v-model="serviceForm.active" type="checkbox" class="sr-only" />
                                        <div class="w-10 h-5 bg-gray-200 dark:bg-gray-700 rounded-full transition-all" :class="serviceForm.active ? 'bg-primary-600 dark:bg-primary-600' : ''"></div>
                                        <div class="absolute left-1 top-1 w-3 h-3 bg-white rounded-full transition-all transform" :class="serviceForm.active ? 'translate-x-5' : ''"></div>
                                    </div>
                                    <span class="ml-3 text-[11px] font-bold text-gray-400 dark:text-gray-500 group-hover:text-gray-600 transition-colors">Operational</span>
                                </label>
                                <label class="flex items-center group cursor-pointer">
                                    <div class="relative">
                                        <input v-model="serviceForm.featured" type="checkbox" class="sr-only" />
                                        <div class="w-10 h-5 bg-gray-200 dark:bg-gray-700 rounded-full transition-all" :class="serviceForm.featured ? 'bg-amber-500' : ''"></div>
                                        <div class="absolute left-1 top-1 w-3 h-3 bg-white rounded-full transition-all transform" :class="serviceForm.featured ? 'translate-x-5' : ''"></div>
                                    </div>
                                    <span class="ml-3 text-[11px] font-bold text-gray-400 dark:text-gray-500 group-hover:text-gray-600 transition-colors">Featured</span>
                                </label>
                                <label class="flex items-center group cursor-pointer">
                                    <div class="relative">
                                        <input v-model="serviceForm.public_status" type="checkbox" class="sr-only" />
                                        <div class="w-10 h-5 bg-gray-200 dark:bg-gray-700 rounded-full transition-all" :class="serviceForm.public_status ? 'bg-blue-600' : ''"></div>
                                        <div class="absolute left-1 top-1 w-3 h-3 bg-white rounded-full transition-all transform" :class="serviceForm.public_status ? 'translate-x-5' : ''"></div>
                                    </div>
                                    <span class="ml-3 text-[11px] font-bold text-gray-400 dark:text-gray-500 group-hover:text-gray-600 transition-colors">Public Visibility</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div class="space-y-6">
                        <h3 class="text-xs font-bold text-primary-600 uppercase tracking-widest border-b border-gray-50 dark:border-gray-700/30 pb-4">Service Description</h3>
                        <div class="space-y-6">
                            <div>
                                <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 px-1">Short Description*</label>
                                <input v-model="serviceForm.short_description" type="text" required class="w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3.5 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium" />
                            </div>
                            <div>
                                <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 px-1">Full Service Details*</label>
                                <textarea v-model="serviceForm.long_description" rows="5" required class="w-full bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl py-3.5 px-5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium" placeholder="Describe the service in detail..."></textarea>
                            </div>
                        </div>
                    </div>

                    <div class="flex justify-end space-x-4 pt-10 border-t border-gray-50 dark:border-gray-700/30">
                        <button type="button" @click="closeModal" class="px-8 py-4 border border-gray-100 dark:border-gray-700 rounded-2xl text-xs font-bold text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all">
                            Cancel
                        </button>
                        <button type="submit" :disabled="isSubmitting" class="px-10 py-4 bg-primary-600 text-white rounded-2xl text-xs font-bold hover:bg-primary-700 transition-all shadow-xl shadow-primary-500/20 disabled:opacity-50 active:scale-95">
                            {{ isSubmitting ? "Saving..." : "Save Service" }}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </AppSidebar>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { useServiceStore } from "@/stores/services";
import { useUIStore } from "@/stores/ui";
import { useAuthStore } from "@/stores/auth";
import { PlusIcon, PencilSquareIcon, TrashIcon } from "@heroicons/vue/24/outline";
import AppSidebar from "@/components/layout/AppSidebar.vue";

const serviceStore = useServiceStore();
const uiStore = useUIStore();
const authStore = useAuthStore();

// State
const showAddServiceModal = ref(false);
const editingService = ref(null);
const isSubmitting = ref(false);

// Form validation errors
const errors = ref({
    starting_price: "",
    min_weeks: "",
    max_weeks: "",
});

// Service form
const serviceForm = reactive({
    name: "",
    category_ids: [],
    category_id: "",
    short_description: "",
    long_description: "",
    pricing: {
        starting_price: 0,
    },
    timeline: {
        min_weeks: 0,
        max_weeks: 0,
    },
    active: true,
    featured: false,
    public_status: true,
});

// Initialize
onMounted(async () => {
    // Check if user is authenticated and has admin role
    await authStore.initializeAuth();

    if (!authStore.isAuthenticated) {
        uiStore.showNotification({
            type: "error",
            title: "Authentication Required",
            message: "Please log in to access this page.",
        });
        return;
    }

    if (!authStore.hasRole("admin")) {
        uiStore.showNotification({
            type: "error",
            title: "Access Denied",
            message: "You don't have permission to access this page.",
        });
        return;
    }

    await serviceStore.fetchAdminServices();
    await serviceStore.fetchCategories();
});

// Methods
const editService = (service) => {
    editingService.value = service;
    Object.assign(serviceForm, {
        ...service,
        category_ids: service.categories ? service.categories.map(c => c.id) : (service.category_id ? [service.category_id] : []),
        category_id: service.category_id,
        pricing: { ...service.pricing },
        timeline: { ...service.timeline },
        active: service.status === "active",
        public_status: service.public_status ? true : false,
    });
    showAddServiceModal.value = true;
};

const closeModal = () => {
    showAddServiceModal.value = false;
    editingService.value = null;
    resetForm();
    clearErrors();
};

const resetForm = () => {
    Object.assign(serviceForm, {
        name: "",
        category_ids: [],
        category_id: "",
        short_description: "",
        long_description: "",
        pricing: { starting_price: 0 },
        timeline: { min_weeks: 0, max_weeks: 0 },
        active: true,
        featured: false,
        public_status: true,
    });
};

// Validation methods
const validateStartingPrice = () => {
    const price = serviceForm.pricing.starting_price;
    if (price < 0) {
        errors.value.starting_price =
            "Price must be greater than or equal to 0";
    } else {
        errors.value.starting_price = "";
    }
};

const validateTimeline = () => {
    const minWeeks = serviceForm.timeline.min_weeks;
    const maxWeeks = serviceForm.timeline.max_weeks;

    if (minWeeks < 1) {
        errors.value.min_weeks = "Minimum weeks must be at least 1";
    } else {
        errors.value.min_weeks = "";
    }

    if (maxWeeks < 1) {
        errors.value.max_weeks = "Maximum weeks must be at least 1";
    } else if (maxWeeks < minWeeks) {
        errors.value.max_weeks =
            "Maximum weeks must be greater than or equal to minimum weeks";
    } else {
        errors.value.max_weeks = "";
    }
};

const validateForm = () => {
    validateStartingPrice();
    validateTimeline();

    return (
        !errors.value.starting_price &&
        !errors.value.min_weeks &&
        !errors.value.max_weeks
    );
};

const clearErrors = () => {
    errors.value = {
        starting_price: "",
        min_weeks: "",
        max_weeks: "",
    };
};

const saveService = async () => {
    if (!validateForm()) {
        uiStore.showNotification({
            type: "error",
            title: "Validation Error",
            message: "Please fix the validation errors before saving.",
        });
        return;
    }

    isSubmitting.value = true;

    try {
        const { categories, packages, ...formData } = serviceForm;
        const payload = {
            name: formData.name,
            category_ids: formData.category_ids,
            short_description: formData.short_description,
            long_description: formData.long_description,
            starting_price: formData.pricing.starting_price,
            timeline_min_weeks: formData.timeline.min_weeks,
            timeline_max_weeks: formData.timeline.max_weeks,
            status: formData.active ? "active" : "inactive",
            featured: formData.featured,
            public_status: formData.public_status,
        };

        if (editingService.value) {
            await serviceStore.updateService(editingService.value.id, payload);
            uiStore.showNotification({
                type: "success",
                title: "Success",
                message: "Service updated successfully",
            });
        } else {
            await serviceStore.addService(payload);
            uiStore.showNotification({
                type: "success",
                title: "Success",
                message: "Service created successfully",
            });
        }
        // Refresh data to ensure UI is updated
        await serviceStore.fetchAdminServices();
        closeModal();
    } catch (error) {
        console.error("Failed to save service:", error);
        uiStore.showNotification({
            type: "error",
            title: "Error",
            message: `Failed to ${editingService.value ? "update" : "create"} service. Please try again.`,
        });
    } finally {
        isSubmitting.value = false;
    }
};

const toggleServiceStatus = async (service) => {
    try {
        await serviceStore.updateService(service.id, {
            status: service.status === "active" ? "inactive" : "active",
        });
        uiStore.showNotification({
            type: "success",
            title: "Success",
            message: "Service status updated successfully",
        });
        // Refresh data to ensure UI is updated
        await serviceStore.fetchAdminServices();
    } catch (error) {
        console.error("Failed to toggle service status:", error);
        uiStore.showNotification({
            type: "error",
            title: "Error",
            message: "Failed to update service status. Please try again.",
        });
    }
};

const togglePublic = async (service) => {
    try {
        await serviceStore.togglePublicStatus(service.id);
        uiStore.showNotification({
            type: "success",
            title: "Protocol Update",
            message: `Service visibility set to ${!service.public_status ? 'Public' : 'Private'}`,
        });
        await serviceStore.fetchAdminServices();
    } catch (error) {
        console.error("Failed to toggle public status:", error);
    }
};

const deleteService = async (service) => {
    if (confirm(`Are you sure you want to delete "${service.name}"?`)) {
        try {
            await serviceStore.deleteService(service.id);
            uiStore.showNotification({
                type: "success",
                title: "Success",
                message: "Service deleted successfully",
            });
            // Refresh data to ensure UI is updated
            await serviceStore.fetchAdminServices();
        } catch (error) {
            console.error("Failed to delete service:", error);
            uiStore.showNotification({
                type: "error",
                title: "Error",
                message: "Failed to delete service. Please try again.",
            });
        }
    }
};
</script>
